import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCollectionDetails, getCollectionDetections } from '@utils/api/collections';
import { useAuth } from '@contexts/User.context';
import { useProject } from '@contexts/Project.context';
import { useObjects } from '@hooks/useObjects';
import { collectionConfigKey } from '@utils/collectionQueries';
import { buildExtendedFilters, buildCollectionFilters, toHexColor } from '../utils/transformUtils';
import { colorForKey } from '@utils/objectColor';
import { buildShapeAvailability } from '../utils/collectionMapSource';
import { readHexagonConfig } from './useHexagonConfig';
import { DEFAULT_DETECTION_POINTER, DETECTION_POINTERS } from '../constants';

/**
 * Pointers are validated once, here, so everything downstream can treat the value
 * as one of the three known shapes. An unrecognized string would otherwise
 * survive `??` and be read differently by each consumer — the picker would show
 * no glyph, and the map would draw it as an outline.
 */
const toPointer = value => (DETECTION_POINTERS.includes(value) ? value : undefined);

/**
 * Fetches and merges collection data from two separate endpoints.
 *
 * Architecture:
 * - Collection config query: name, detection objects, saved filters/styles
 *   (invalidated when user updates filters/styling)
 * - Detection results query: features, metadata, parameter ranges
 *   (stable, not invalidated by filter updates)
 *
 * The hook merges both responses into a flat, UI-ready structure with
 * extended filter ranges that accommodate saved values from other projects.
 *
 * Coordinate space:
 * - Orthophoto collections request projection '3857' so features arrive in
 *   EPSG:3857 and can be added to the OL map directly without any transformation.
 * - Single-image collections use 'normalized' (the backend default for that mode)
 *   so features can be matched to individual drone images by tile_name.
 * - The effective projection is echoed back as `metadata.projection` and exposed
 *   on the merged data object for consumers to assert if needed.
 *
 * @param {string} projectUuid    - Current project UUID
 * @param {string} collectionUuid - Selected collection UUID
 * @param {string} [projection]   - Coordinate space to request: '3857' (default) or 'normalized'
 * @returns {{ data: object|null, isLoading: boolean, error: unknown }}
 */
const useCollectionData = (projectUuid, collectionUuid, projection = '3857') => {
	// The measurement system affects unit labels and conversion factors baked into
	// filter metadata. Reading it here makes the memo reactive to system changes.
	const { currentUser } = useAuth();
	const measurementSystem = currentUser?.measurement_system ?? 'metric';

	// Taxonomy supplies each object's recommended shape. Shared warm cache with the
	// Objects page; a failure is non-fatal (objects fall back to the default
	// pointer). Its loading state feeds the gate at the bottom of this hook.
	const { projectMode } = useProject();
	const {
		nodes: taxonomyNodes,
		isLoading: taxonomyLoading,
		isError: taxonomyError,
	} = useObjects(projectMode);
	// Detection results identify objects by their taxonomy node uuid.
	const nodesByUuid = useMemo(
		() => Object.fromEntries(taxonomyNodes.map(node => [node.uuid, node])),
		[taxonomyNodes]
	);

	// Query 1: Collection config (name, objects, saved filters/styles)
	const {
		data: config,
		isLoading: configLoading,
		error: configError,
	} = useQuery({
		// Must stay the key the writes read back through
		// `saveDetectionObjectPatches`: if the two ever diverge its
		// `getQueryData` returns undefined and the PATCH is built from the
		// detection results alone, deleting every config-only object
		// server-side. Hence one spelling, shared.
		queryKey: collectionConfigKey(collectionUuid),
		queryFn: () => getCollectionDetails(collectionUuid),
		enabled: !!collectionUuid,
		staleTime: 5 * 60 * 1000, // Config doesn't change often
	});

	// Query 2: Detection results (features, metadata, parameter ranges)
	// projection is included in the query key so React Query refetches if it changes
	const {
		data: detections,
		isLoading: detectionsLoading,
		error: detectionsError,
	} = useQuery({
		queryKey: ['collectionDetections', projectUuid, collectionUuid, projection],
		queryFn: () => getCollectionDetections(projectUuid, collectionUuid, projection),
		enabled: !!projectUuid && !!collectionUuid,
		staleTime: 10 * 60 * 1000, // Detection results are stable
		retry: false, // 404 means no detections exist yet — retrying won't help
	});

	// Which shapes each object's detections can actually draw. Kept in its own
	// memo, keyed on the features array: the merge below re-runs on every config
	// refetch (a color save, a pointer pick), and this scan has no business
	// repeating for those — the detection results are a separate query that
	// doesn't change when stored config does.
	const shapeAvailability = useMemo(
		() => buildShapeAvailability(detections?.results?.features),
		[detections?.results?.features]
	);

	// Merge and transform for UI consumption
	const merged = useMemo(() => {
		if (!config || !detections) return null;

		// Build objects array with merged data from both endpoints
		const objects = (detections.metadata?.objects ?? []).map(detObj => {
			const configObj = (config.detection_objects ?? []).find(
				co => co.node_uuid === detObj.node_uuid
			);
			const defaultPointer =
				toPointer(nodesByUuid[detObj.node_uuid]?.detection_pointer) ??
				DEFAULT_DETECTION_POINTER;
			const savedPointer = toPointer(configObj?.style?.detection_pointer);

			return {
				key: detObj.node_uuid,
				// The collection's own name for the object, from its stored config —
				// the same name the Collections page shows. The detection results'
				// name covers an object the config hasn't caught up with yet, then the
				// taxonomy node's name, so a raw uuid is the last resort.
				label:
					configObj?.name ||
					detObj.name ||
					nodesByUuid[detObj.node_uuid]?.name ||
					detObj.node_uuid,
				count: detObj.count,

				// From config (styling + saved filters). Normalized here for the
				// same reason pointers are: one boundary decides the form, so no
				// consumer has to re-validate it. See `toHexColor`.
				// Unstyled objects take their taxonomy colour, then a stable per-key hue as the
				// Collections page does, rather than all sharing the red fallback.
				color:
					toHexColor(configObj?.style?.color) ??
					toHexColor(nodesByUuid[detObj.node_uuid]?.color) ??
					toHexColor(colorForKey(detObj.node_uuid)),
				opacity: configObj?.style?.opacity ?? 100,
				isActive: configObj?.is_active ?? true,
				savedFilters: configObj?.filters || {},
				// Raw stored style, so a save can spread it forward instead of
				// rebuilding it field by field and dropping whatever it forgot.
				style: configObj?.style ?? {},

				// Detection pointer — the shape this object draws as on the map.
				// `defaultPointer` is the taxonomy's recommendation and
				// `savedPointer` the user's override; they stay separate because
				// the picker badges which option is the default, and clearing an
				// override means removing it rather than writing the default in.
				defaultPointer,
				savedPointer,
				pointer: savedPointer ?? defaultPointer,

				// Per-shape counts for this object, so the picker can say when a
				// shape isn't available in this run instead of silently falling back.
				availability: shapeAvailability[detObj.node_uuid],

				// From detections (available ranges)
				parameters: detObj.parameters,

				// Computed filters with extended ranges
				filters: buildExtendedFilters(detObj.parameters, configObj?.filters),
			};
		});

		return {
			// Metadata
			uuid: config.uuid,
			name: config.name,
			mode: config.mode,
			// Echoed back by the backend — consumers can assert the coordinate space
			projection: detections.metadata?.projection ?? projection,

			// Objects (merged config + detection data)
			objects,

			// Raw detection_objects from config — includes entries with zero
			// detections that are absent from `objects`. Needed so mutations that
			// replace the entire detection_objects array can preserve config-only
			// entries (saved filters/styles with no current detections).
			configDetectionObjects: config.detection_objects ?? [],

			// Collection-level filters (aggregated with extended ranges)
			collectionFilters: buildCollectionFilters(
				detections.metadata?.objects ?? [],
				config.filters
			),

			// Raw persisted collection-level filters ({ [parameterId]: { min, max } }
			// in raw API units). Exposed for the hexagon filter body, which sources
			// from applied/persisted filters rather than the live slider draft.
			savedCollectionFilters: config.filters ?? {},

			// The collection's saved hexagon display settings ({ size, color }),
			// normalized here for the same reason colors and pointers are: one
			// boundary decides the form, and the stored field is free-form so
			// nothing downstream should be re-validating it. See readHexagonConfig.
			hexagonConfig: readHexagonConfig(config.hexagon),

			// Counts — total_count from results metadata; fall back to summing
			// per-object counts in case the field is absent or zero.
			totalCount:
				detections.results?.metadata?.total_count ||
				objects.reduce((sum, obj) => sum + (obj.count ?? 0), 0),
			objectClassCount: objects.length,

			// GeoJSON features (kept as-is for map rendering)
			features: detections.results?.features ?? [],
		};
	}, [config, detections, measurementSystem, nodesByUuid, shapeAvailability]);

	// Nothing is loading for a collection that hasn't been selected. The two
	// queries above are already gated on `collectionUuid`; the taxonomy query is
	// not — it is per project type and shared with the Objects page — so without
	// this its first fetch would report the panel as loading on every project
	// open, flashing the map's "Loading detections" pill with no collection in
	// the URL. `merged` is null in that state anyway.
	const hasCollection = Boolean(collectionUuid);

	return {
		data: merged,
		// Waiting on taxonomy avoids drawing everything as center points for a
		// frame and then visibly re-shaping. But it is only worth waiting while
		// it might still succeed — the query retries with backoff, and blocking
		// the whole panel for seconds on an endpoint that is down contradicts
		// treating a taxonomy failure as non-fatal.
		isLoading:
			hasCollection &&
			(configLoading || detectionsLoading || (taxonomyLoading && !taxonomyError)),
		error: configError || detectionsError,
	};
};

export default useCollectionData;
